import React, { useState, useEffect } from 'react';
import { startCase, uniq } from 'lodash';
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  Legend,
  XAxis,
  Line,
  YAxis,
} from 'recharts';
import { Formatter } from '../../../utils';
import getCustomTooltip from './CustomTooltip';
import { COLORS, mapSumsToAxis, normalizeDataByDate } from './graphUtils';
import './styles.scoped.css';

const Tooltip = getCustomTooltip();

export interface LineGraphProps {
  data: any;
  sums: string[];
  groups: string[];
  dateRange: [Date, Date];
  isFunnel?: boolean;
  hideYAxisTicks?: boolean;
}

function LineGraph({ data, sums, groups, dateRange, hideYAxisTicks = true }: LineGraphProps) {
  const [yAxisMap, setYAxisMap] = useState<any>({});
  const [graphData, setGraphData] = useState<any[]>([]);

  useEffect(() => {
    setYAxisMap(mapSumsToAxis(sums));
  }, [sums]);

  useEffect(() => {
    setGraphData(normalizeDataByDate(data, sums, groups[0], dateRange));
  }, [data, dateRange, groups, sums]);

  const group = 'dateString';
  const uniqueYAxis: any[] = uniq(Object.values(yAxisMap));

  return (
    <ResponsiveContainer>
      <LineChart data={graphData}>
        <CartesianGrid strokeDasharray="2 2" />
        {Tooltip}
        <Legend
          iconType="circle"
          formatter={(val) => (
            <span className="legend-label">{Formatter.startCaseWithSpecialChars(val)}</span>
          )}
          iconSize={12}
        />
        <XAxis dataKey={group} tick={{ fontSize: 12 }} tickSize={8} scale="band" tickCount={6} />
        {uniqueYAxis.map((id, index) => (
          <YAxis
            key={`yaxis-${id}`}
            yAxisId={id}
            hide={hideYAxisTicks || (!hideYAxisTicks && index > 1)}
            orientation={index === 0 ? 'left' : 'right'}
            allowDecimals
          />
        ))}
        {sums.map((sum, i) => (
          <Line
            key={sum}
            dataKey={sum}
            yAxisId={yAxisMap[sum]}
            strokeWidth={2}
            stroke={COLORS.Line[i % COLORS.Line.length]}
            name={startCase(sum)}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default LineGraph;
